import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { Button } from '@deepguide-ai/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog';
import { queryClient, trpc } from '@/lib/trpc';
import { vendorLogoPath } from '@/lib/vendor-logo';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle';

function SnowflakeConfigModal({ open, onOpenChange, integration, workspaceId, onComplete }: ConfigModalProps) {
  const [connecting, setConnecting] = useState(false);
  const integrationId = integration?.id ?? '';
  const status = useQuery({ ...trpc.integrations.getSnowflakeConnectionStatus.queryOptions({ integrationId }), enabled: open && !!integrationId });
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='sm:max-w-xl'>
      <DialogHeader>
        <DialogTitle>Snowflake evidence</DialogTitle>
        <DialogDescription>Collect account access and 30-day login history for evidence review.</DialogDescription>
      </DialogHeader>
      <ScrollArea type='scroll' className='max-h-[65vh]' viewClassName='max-h-[65vh] [&>div]:!block'>
      <div className='space-y-3 px-6 pb-4 text-sm'>
        {integrationId && status.isLoading ? <p role='status'>Loading Snowflake connection…</p> : null}
        {status.error ? <div className='space-y-2'>
          <p role='alert' className='text-destructive'>{status.error.message}</p>
          <Button variant='outline' disabled={status.isFetching} onClick={() => void status.refetch()}>Retry loading</Button>
        </div> : status.data ? <dl className='space-y-1'><div><dt className='text-muted-foreground'>Connected account</dt><dd>{status.data.providerAccount}</dd></div><div><dt className='text-muted-foreground'>Role</dt><dd>{status.data.role}</dd></div><div><dt className='text-muted-foreground'>Warehouse</dt><dd>{status.data.warehouse}</dd></div></dl> : null}
        <p className='text-muted-foreground'>Use Scan on the Snowflake card to collect evidence and view progress and results. Use Access reviews to review collected accounts. Connecting alone does not collect evidence.</p>
        <p className='text-muted-foreground'>Account Usage can lag changes by up to two hours. Role inheritance, object scope and unresolved grants need review. MFA enrollment and visible authentication policies are collected where authorized. Effective MFA and SSO enforcement still require review.</p>
        <p className='text-muted-foreground'>A platform operator must configure the Snowflake OAuth app, read role and warehouse before connecting. Collection queries can consume warehouse credits. Policy inspection requires additional existing permissions; a successful connection does not grant them.</p>
        {status.data?.lastError ? <p role='alert' className='text-destructive'>{status.data.lastError}</p> : null}
        <p className='text-muted-foreground'>Disconnect from the integration card to stop future collection. Collected evidence is retained. Revoke app access in Snowflake separately.</p>
      </div>
      </ScrollArea>
      <DialogFooter>
        <Button disabled={connecting} onClick={async () => {
          setConnecting(true);
          try { await SnowflakeProvider.handleConnect({ workspaceId, onSuccess: onComplete, onFinish: () => setConnecting(false) }); }
          catch { setConnecting(false); }
        }}>{connecting ? 'Connecting…' : integrationId ? 'Reconnect Snowflake' : 'Connect Snowflake'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

export const SnowflakeProvider: IntegrationProvider = {
  id: 'Snowflake', name: 'Snowflake', description: 'Account users, roles and grants',
  Icon: () => <img src={vendorLogoPath('snowflake')} alt='' className='size-6 object-contain' />,
  ConfigModal: SnowflakeConfigModal, postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    { id: 'authentication', name: 'Authentication settings', description: 'Read visible policy attachments and security integrations where authorized', enabled: true },
    { id: 'audit', name: 'Login history', description: 'Read the preceding 30 days of available login attempts', enabled: true },
    { id: 'access', name: 'Access inventory', description: 'Read Account Usage users, roles, direct assignments and scoped grants', enabled: true },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.initSnowflakeAuth.mutationOptions()).execute(undefined).catch(error => {
      toast.error(error instanceof Error ? error.message : 'Could not start Snowflake authorization');
      onFinish?.('error');
      throw error;
    });
    openOAuthConnectPopup({ authUrl: result.authUrl, windowName: 'snowflake-auth', provider: 'Snowflake', onSuccess, onFinish,
      onError: message => toast.error(message), onPopupBlocked: () => toast.error('Allow popups to connect Snowflake.'),
    });
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.completeSnowflakeAuth.mutationOptions()).execute({ code, state });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getSnowflakeConnectionStatus.queryKey() }),
    ]);
    return result;
  },
  syncData: async () => { throw new Error('Use Scan on the Snowflake integration card to collect access evidence.'); },
};
